import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/About.css';

// Importar imágenes de servicios
import serviceMining from '../assets/services/mining.webp';
import serviceSecurity from '../assets/services/security.webp';
import serviceTransport from '../assets/services/transporte.webp';
import serviceExploration from '../assets/services/exploration.webp';
import logisticsVideo from '../assets/services/logistics.webm';
import securityVideo from '../assets/services/security.webm';
import industryVideo from '../assets/services/industry.webm';

// Importar imágenes para las tarjetas de características
import nomadaImage from '../assets/nomada.webp';
import communityImage from '../assets/community.webp';
import expansionImage from '../assets/expansion.webp';

const About = () => {
    const { t } = useTranslation();
    const [activeService, setActiveService] = useState(null);

    const features = [
        {
            key: "fleet",
            icon: "ship",
            image: nomadaImage
        },
        {
            key: "community",
            icon: "users",
            image: communityImage
        },
        {
            key: "expansion",
            icon: "globe",
            image: expansionImage
        }
    ];

    const services = [
        {
            key: "industry",
            image: serviceMining,
            icon: "industry",
            detailImage: industryVideo,
            isVideo: true
        },
        {
            key: "security",
            image: serviceSecurity,
            icon: "shield-alt",
            detailImage: securityVideo,
            isVideo: true
        },
        {
            key: "transport",
            image: serviceTransport,
            icon: "truck",
            detailImage: logisticsVideo,
            isVideo: true
        },
        {
            key: "exploration",
            image: serviceExploration,
            icon: "compass",
            detailImage: serviceExploration
        }
    ];

    const toggleServiceDetail = (key) => {
        if (activeService === key) {
            setActiveService(null);
        } else {
            setActiveService(key);
        }
    };

    return (
        <>
            <section id="historia" className="about-section">
                <div className="about-content">
                    <h2>{t('about.title')}</h2>
                    
                    {/* Extracto del libro */}
                    <div className="book-extract">
                        <div className="book-header">
                            <h3 className="book-title">{t('about.bookExtract.title')}</h3>
                            <p className="book-subtitle">{t('about.bookExtract.subtitle')}</p>
                        </div>
                        <div className="book-content">
                            {t('about.bookExtract.content').split('\n\n').map((paragraph, index) => (
                                <p key={index} className="book-paragraph">
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    </div>
                    
                    <div className="about-text">
                        <p>{t('about.description1')}</p>
                        <p>{t('about.description2')}</p>
                    </div>
                    
                    <div className="features-grid">
                        {features.map((feature) => (
                            <div key={feature.key} className="feature-card">
                                <div className="feature-icon">
                                    <i className={`fas fa-${feature.icon}`}></i>
                                </div>
                                <div className="feature-image">
                                    <img src={feature.image} alt={t(`about.features.${feature.key}.title`)} />
                                </div>
                                <h3>{t(`about.features.${feature.key}.title`)}</h3>
                                <p>{t(`about.features.${feature.key}.description`)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section id="servicios" className="services-section">
                <div className="parallax-background"></div>
                <div className="services-content">
                    <div className="services-intro">
                        <h2>{t('services.title')}</h2>
                        <h3>{t('services.subtitle', 'Tu conexión en las estrellas')}</h3>
                        <p>{t('services.intro1', 'Corvus Nocta es un pilar fundamental en la vida de muchos ciudadanos a lo largo del imperio UEE.')}</p>
                        <p>{t('services.intro2', 'Nuestros pilotos profesionales, tripulantes y trabajadores están listos para enfrentar cualquier desafío y proporcionar servicios de primera clase que generalmente están reservados para ejecutivos corporativos o personal militar.')}</p>
                    </div>
                    
                    <div className="services-grid">
                        {services.map((service) => (
                            <div key={`service-wrapper-${service.key}`} className="service-wrapper">
                                <div 
                                    className={`service-card ${activeService === service.key ? 'collapsed' : ''}`}
                                    onClick={() => activeService !== service.key && toggleServiceDetail(service.key)}
                                >
                                    <div className="service-status">{t('services.status')}</div>
                                    <div className="service-image">
                                        <img src={service.image} alt={t(`services.items.${service.key}.title`)} />
                                    </div>
                                    <div className="service-icon">
                                        <i className={`fas fa-${service.icon}`}></i>
                                    </div>
                                    <h3>{t(`services.items.${service.key}.title`)}</h3>
                                    <p>{t(`services.items.${service.key}.description`)}</p>
                                    <button 
                                        className="service-more"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleServiceDetail(service.key);
                                        }}
                                    >
                                        {t('services.more', 'Más')}
                                    </button>
                                </div>

                                <div 
                                    className={`service-detail ${activeService === service.key ? 'active' : ''}`}
                                >
                                    <div className="detail-container">
                                        <div className="detail-image">
                                            {service.isVideo && activeService === service.key ? (
                                                <video autoPlay loop muted playsInline>
                                                    <source src={service.detailImage} type="video/webm" />
                                                </video>
                                            ) : !service.isVideo ? (
                                                <img src={service.detailImage} alt={t(`services.items.${service.key}.title`)} />
                                            ) : null}
                                        </div>
                                        <div className="detail-content">
                                            <h2>{t(`services.items.${service.key}.title`)}</h2>
                                            {service.key === 'industry' ? (
                                                <div className="industry-details">
                                                    <div className="industry-detail-item">
                                                        <h3>{t('services.items.industry.details.mining.title')}</h3>
                                                        <p>{t('services.items.industry.details.mining.description')}</p>
                                                    </div>
                                                    <div className="industry-detail-item">
                                                        <h3>{t('services.items.industry.details.refining.title')}</h3>
                                                        <p>{t('services.items.industry.details.refining.description')}</p>
                                                    </div>
                                                    <div className="industry-detail-item">
                                                        <h3>{t('services.items.industry.details.salvage.title')}</h3>
                                                        <p>{t('services.items.industry.details.salvage.description')}</p>
                                                    </div>
                                                </div>
                                            ) : service.key === 'transport' ? (
                                                <div className="industry-details">
                                                    <div className="industry-detail-item">
                                                        <h3>{t('services.items.transport.details.transport.title')}</h3>
                                                        <p>{t('services.items.transport.details.transport.description')}</p>
                                                    </div>
                                                    <div className="industry-detail-item">
                                                        <h3>{t('services.items.transport.details.logistics.title')}</h3>
                                                        <p>{t('services.items.transport.details.logistics.description')}</p>
                                                    </div>
                                                </div>
                                            ) : service.key === 'exploration' ? (
                                                <div className="service-details">
                                                    <div className="service-detail-item">
                                                        <h3>{t('services.items.exploration.details.exploration.title')}</h3>
                                                        <p>{t('services.items.exploration.details.exploration.description')}</p>
                                                    </div>
                                                    <div className="service-detail-item">
                                                        <h3>{t('services.items.exploration.details.science.title')}</h3>
                                                        <p>{t('services.items.exploration.details.science.description')}</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <p>{t(`services.items.${service.key}.details.${service.key}.description`)}</p>
                                                </>
                                            )}
                                            <button 
                                                className="service-more"
                                                onClick={() => toggleServiceDetail(service.key)}
                                            >
                                                {t('services.close', 'Cerrar')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

export default About;